import React, { useState, useEffect } from 'react';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useModerator } from '../hooks/useModerator';
import { useNavigate, Link } from 'react-router-dom';
import { ProtectedRoute } from '../auth/ProtectedRoute';
import { formatRelativeTime } from '../utils/dateUtils';
import { Button } from '../components/common/Button';
import { Modal } from '../components/common/Modal';
import { Textarea } from '../components/common/Textarea';
import { moderationApi } from '../api/moderation';
import type { Comment, CommentPaginatedResponse } from '../types/comment';
import { useLanguage } from '../i18n/useLanguage';

const DeletedCommentsPageContent: React.FC = () => {
  const { language } = useLanguage();
  const isModerator = useModerator();
  const navigate = useNavigate();
  const [currentPage, _setCurrentPage] = useState(0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [_pagination, setPagination] = useState<{ totalElements: number; totalPages: number }>({ totalElements: 0, totalPages: 0 });
  const [permanentDeleteCommentId, setPermanentDeleteCommentId] = useState<string | null>(null);
  const [restoreCommentId, setRestoreCommentId] = useState<string | null>(null);
  const [restoreReason, setRestoreReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  const fetchDeletedComments = async (page: number = 0, size: number = 50) => {
    try {
      setLoading(true);
      setError(null);
      const response: CommentPaginatedResponse = await moderationApi.getDeletedComments(page, size);
      setComments(response.content);
      setPagination({
        totalElements: response.totalElements || 0,
        totalPages: response.totalPages || 0,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch deleted comments';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isModerator) {
      navigate('/');
      return;
    }

    fetchDeletedComments(currentPage, 50);
  }, [currentPage, isModerator, navigate]);

  const handlePermanentDelete = async (commentId: string) => {
    setPermanentDeleteCommentId(commentId);
    setDeleteError(null);
  };

  const confirmPermanentDelete = async () => {
    if (!permanentDeleteCommentId) return;

    setDeleting(true);
    setDeleteError(null);

    try {
      await moderationApi.permanentlyDeleteComment(permanentDeleteCommentId);
      // Refresh the list
      await fetchDeletedComments(currentPage, 50);
      setPermanentDeleteCommentId(null);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to permanently delete comment');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (commentId: string) => {
    setRestoreCommentId(commentId);
    setRestoreReason('');
    setRestoreError(null);
  };

  const confirmRestore = async () => {
    if (!restoreCommentId || !restoreReason.trim()) {
      setRestoreError('Reason is required');
      return;
    }

    setRestoring(true);
    setRestoreError(null);

    try {
      await moderationApi.restoreComment(restoreCommentId, {
        reason: restoreReason.trim(),
      });
      // Refresh the list
      await fetchDeletedComments(currentPage, 50);
      setRestoreCommentId(null);
      setRestoreReason('');
    } catch (err) {
      setRestoreError(err instanceof Error ? err.message : 'Failed to restore comment');
    } finally {
      setRestoring(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!isModerator) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return <LoadingSpinner size="large" />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  return (
    <div className="deleted-posts-page">
      <div className="deleted-posts-page-header">
        <h1>Deleted Comments</h1>
        <p className="deleted-posts-description">
          View all soft-deleted comments. Only visible to moderators.
        </p>
      </div>

      {comments.length === 0 ? (
        <div className="deleted-posts-empty">
          <p>No deleted comments found.</p>
        </div>
      ) : (
        <div className="deleted-posts-table-container">
          <table className="deleted-posts-table">
            <thead>
              <tr>
                <th>Deletion Reason</th>
                <th>Comment Content</th>
                <th>Post</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {comments.map((comment) => (
                <tr key={comment.id}>
                  <td className="deletion-reason-cell">
                    {comment.deletionReason || <em>No reason provided</em>}
                  </td>
                  <td className="post-content-cell">
                    <div className="post-content-truncated">
                      {truncateText(comment.content, 150)}
                    </div>
                    <div className="comment-meta" style={{ marginTop: '8px', fontSize: '0.875rem', color: '#666' }}>
                      <span>{formatRelativeTime(comment.createdAt, language)}</span>
                      {comment.authorUsername && comment.authorUsername.trim() && (
                        <>
                          <span> by </span>
                          <span>{comment.authorUsername}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="post-link-cell">
                    <Link to={`/posts/${comment.postId}`} className="post-link">
                      View Post
                    </Link>
                  </td>
                  <td className="actions-cell">
                    <div className="deleted-posts-actions">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleRestore(comment.id)}
                      >
                        Restore
                      </Button>
                      <Button
                        variant="danger"
                        size="small"
                        onClick={() => handlePermanentDelete(comment.id)}
                      >
                        Delete Permanently
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={permanentDeleteCommentId !== null}
        onClose={() => {
          setPermanentDeleteCommentId(null);
          setDeleteError(null);
        }}
        title="Permanently Delete Comment"
      >
        <div className="permanent-delete-modal">
          <p>
            <strong>Warning:</strong> This action cannot be undone. The comment will be permanently removed from the database.
          </p>
          <p>Are you sure you want to permanently delete this comment?</p>
          {deleteError && <div className="error-message">{deleteError}</div>}
          <div className="modal-actions">
            <Button
              variant="danger"
              onClick={confirmPermanentDelete}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Permanently'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setPermanentDeleteCommentId(null);
                setDeleteError(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={restoreCommentId !== null}
        onClose={() => {
          setRestoreCommentId(null);
          setRestoreReason('');
          setRestoreError(null);
        }}
        title="Restore Comment"
      >
        <div className="restore-modal">
          <p>Are you sure you want to restore this comment?</p>
          <Textarea
            label="Reason for restoration"
            value={restoreReason}
            onChange={(e) => setRestoreReason(e.target.value)}
            placeholder="Enter reason for restoration..."
            required
            rows={4}
          />
          {restoreError && <div className="error-message">{restoreError}</div>}
          <div className="modal-actions">
            <Button
              variant="primary"
              onClick={confirmRestore}
              disabled={restoring || !restoreReason.trim()}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setRestoreCommentId(null);
                setRestoreReason('');
                setRestoreError(null);
              }}
              disabled={restoring}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export const DeletedCommentsPage: React.FC = () => {
  return (
    <ProtectedRoute>
      <DeletedCommentsPageContent />
    </ProtectedRoute>
  );
};
